import { Button, Checkbox, TextButton } from "@anna-money/anna-web-ui";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import type { ChangeEvent, FC, MouseEvent } from "react";
import { gaEventName, gaSendUserEvent } from "../../../analytics/analyticsManager";
import { CardPaymentNotAllowedModal } from "../../../components/cardPaymentNotAllowed/cardPaymentNotAllowedModal";
import type { Form } from "../../../components/form/form";
import { Input } from "../../../components/input/input";
import { currencySymbol } from "../../../helpers/constants";
import { useFactory, useModal } from "../../../helpers/reactHooks";
import type { PaymentPageForm } from "../../../types/paymentPage";
import type {
  ObPaymentPageViewFormType,
  ObPaymentPageViewStore,
} from "../../obPaymentPage/obPaymentPageView/obPaymentPageViewStore";
import { formatValue, parseValue } from "../../paymentLink/components/form/formStore";
import type { Pp2ViewModel } from "../pp2ViewModel";
import type { Pp2CopyFeedback } from "../shared/useCopyFeedback";
import { payAttempt, pressPay } from "./payAttempt";
import { isPayFormValid, maxAmountMessage } from "./payFormValidity";
import { PayProgress } from "./payProgress";
import { Pp2BankDetails } from "./pp2BankDetails";
import { Pp2ReadOnlyText } from "./pp2ReadOnlyText";
import styles from "./pp2PayForm.module.scss";

const PRIVACY_POLICY_URL = "https://anna.money/privacy-policy";
const PAYMENTS_FAQ_URL = "https://anna.money/blog/updates/your-anna-payment-link-a-new-handy-way-to-accept-payments#p2";

/**
 * Contents of the form column (spec 3.4 items 1–9): "Pay this business", Amount, Description,
 * Name/Email, consent, payment error, pay buttons, "More about payments by ANNA", and the
 * "Or pay by bank transfer manually" block with the bank card and its copy button.
 *
 * The shell renders the column itself (`data-test-id="pp2-form"`, flex column, gap 20px), so
 * the items are rendered as direct children (a fragment), in that order.
 *
 * - `store`: the shared ObPaymentPageViewStore (not modified). Its `inProgress` never resets
 *   after a failed payment, so progress is tracked locally by `PayProgress` (owner decision Q16).
 * - `form`: the store's form. The shell has already normalised a fixed amount to "5500.00" (5.2).
 * - `pageData`: raw page data for what the view model does not carry.
 * - `copy`: the page's single copied state; key "bank" with `vm.bank.copyText`.
 *
 * Owner decision Q12: both pay buttons are disabled until the form is valid, checked live on every
 * render by the pure `isPayFormValid` over `form.values` (never `form.isValid()` in render: it
 * writes errors into the form). Over the max amount keeps them disabled and shows today's message.
 */
export type Pp2PayFormProps = {
  vm: Pp2ViewModel;
  pageData: PaymentPageForm;
  form: Form<ObPaymentPageViewFormType>;
  store: ObPaymentPageViewStore;
  copy: Pp2CopyFeedback;
};

export const Pp2PayForm: FC<Pp2PayFormProps> = observer(({ vm, pageData, form, store, copy }) => {
  const progress = useFactory(() => new PayProgress());
  const modal = useModal();
  const { amount, currency, description, name, email, agreement } = form.values;
  const { maxAmount, allowCardPayments, paymentTemplatePublicId, paymentLinkDescription } = pageData;

  const amountError = form.getError("amount") || maxAmountMessage(maxAmount, amount);
  const agreementError = !!form.getError("agreement");
  const formValid = isPayFormValid(form.values, maxAmount);
  const buttonCount = Number(vm.showBankButton) + Number(vm.showCardButton);

  // The buttons keep spinning only when the view is about to redirect (see `pressPay`)
  const pay = (gaEvent: string, transaction: () => Promise<void>): void => {
    void pressPay({
      formValid,
      form,
      progress,
      track: () => {
        gaSendUserEvent(gaEvent);
      },
      attempt: payAttempt(store, transaction),
    });
  };

  const payByBank = (): void => {
    pay(gaEventName.clickConnectByTrueLayer, async () => {
      await store.makeBankTransaction(form, paymentTemplatePublicId);
    });
  };

  const payByCard = (): void => {
    pay(gaEventName.clickConnectByStripe, async () => {
      await store.makeCardTransaction(allowCardPayments, form, paymentTemplatePublicId, modal.open);
    });
  };

  const toggleAgreement = (): void => {
    form.onChange("agreement", !agreement);
  };

  // Spec 5.6: the whole 44px row toggles the consent. The Checkbox's own <label> (which holds the
  // native input and the privacy link) already handles its clicks, so those are left alone.
  const onConsentRowClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (event.target instanceof Element && event.target.closest("label, a")) {
      return;
    }
    toggleAgreement();
  };

  return (
    <>
      <h2 className={styles.title}>Pay this business</h2>
      <Input
        label="Amount"
        className={classNames(styles.field, vm.amountFixed && styles.readOnly)}
        type="number"
        min={0}
        inputMode="decimal"
        placeholder="0.00"
        prefix={currencySymbol[currency]}
        value={amount}
        hasError={!!amountError}
        error={amountError}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (!vm.amountFixed) {
            form.onChange("amount", e.target.value);
          }
        }}
        onBlur={(e) => {
          if (!vm.amountFixed && amount) {
            form.onChange("amount", formatValue(parseValue(e.target.value)));
          }
        }}
        readOnly={vm.amountFixed}
      />
      {vm.descriptionFixed ? (
        <Pp2ReadOnlyText
          label="Description"
          className={styles.field}
          // Display only: the form keeps the bare description, which is what the payment sends
          value={`For ${paymentLinkDescription ?? ""}`}
        />
      ) : (
        <Input
          label="Description"
          className={styles.field}
          value={description}
          error={form.getError("description")}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            form.onChange("description", e.target.value);
          }}
        />
      )}
      <div className={styles.payerRow}>
        <Input
          label="Your name"
          className={styles.field}
          value={name}
          error={form.getError("name")}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            form.onChange("name", e.target.value);
          }}
          name="name"
          autoComplete="name"
        />
        <Input
          label="Email"
          className={styles.field}
          inputMode="email"
          autoCapitalize="none"
          value={email}
          error={form.getError("email")}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            form.onChange("email", e.target.value);
          }}
          name="email"
          autoComplete="email"
        />
      </div>
      {/* The checkbox itself is the keyboard target; the row only widens the pointer hit area */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- pointer-only hit area */}
      <div className={styles.consent} data-test-id="pp2-consent" onClick={onConsentRowClick}>
        <Checkbox
          name="agreement"
          className={styles.checkbox}
          hasError={agreementError}
          checked={agreement}
          onChange={toggleAgreement}
          label={
            <span className={styles.consentText}>
              I agree to{" "}
              <a
                className={classNames(styles.privacyLink, agreementError && styles.error)}
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noreferrer"
              >
                ANNA’s privacy policy
              </a>
            </span>
          }
        />
      </div>
      {store.makeTransactionState === "error" && (
        <div className={styles.paymentError} role="alert">
          Error in creating a payment session
        </div>
      )}
      {buttonCount > 0 && (
        <div
          className={classNames(styles.payButtons, buttonCount === 1 && styles.singlePayButton)}
          data-test-id="pp2-pay-buttons"
        >
          {vm.showBankButton && (
            <Button
              className={styles.payButton}
              type="primary"
              size="l"
              text="Pay by bank transfer"
              disabled={!formValid}
              processing={progress.inProgress}
              onClick={payByBank}
            />
          )}
          {vm.showCardButton && (
            <Button
              className={styles.payButton}
              type="secondary"
              size="l"
              text="Pay by card"
              disabled={!formValid}
              processing={progress.inProgress}
              onClick={payByCard}
            />
          )}
        </div>
      )}
      <span className={styles.faq}>
        <TextButton
          className={styles.faqLink}
          size="l"
          text="More about payments by ANNA"
          href={PAYMENTS_FAQ_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            gaSendUserEvent(gaEventName.clickFaq);
          }}
        />
      </span>
      <Pp2BankDetails bank={vm.bank} copy={copy} />
      <CardPaymentNotAllowedModal visible={modal.visible} onClose={modal.close} name={pageData.beneficiaryName ?? ""} />
    </>
  );
});
